import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Instagram, Facebook, Utensils } from 'lucide-react';

import MenuButtonMobile from 'components/layout/MenuButtonMobile';
import LanguageSwitcher from 'components/ui/LanguageSwitcher';
import { useMenuContext } from 'context/MenuContext';
import { useNavigation } from 'context/NavigationContext';
import useContent from 'context/ContentContext';

/* Animations */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

/* Styled Components */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) => theme.colors.black};
  opacity: 0.95;
  z-index: ${({ theme }) => theme.zIndices.mobileNav};
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const CloseButtonContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: clamp(7rem, 6vw, 8rem);
  background-color: ${({ isScrolled, theme }) =>
    isScrolled ? theme.colors.white : 'transparent'};
  transition: background-color 0.3s ease-in-out;
  z-index: ${({ theme }) => theme.zIndices.navbar};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.layouts.pagePadding};
`;

const NavContent = styled.div`
  margin-top: clamp(7rem, 6vw, 8rem);
  height: 100%;
  ${({ theme }) => theme.mixins.flexColCenter};
  align-items: flex-end;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.layouts.pagePadding};
`;

const NavList = styled.ul`
  ${({ theme }) => theme.mixins.flexColCenter};
  align-items: flex-end;
  gap: 2rem;
`;

const NavItem = styled.li`
  animation: ${slideIn} 0.4s ease-in-out forwards;
  animation-delay: ${({ index }) => `${0.1 + index * 0.1}s`};
  opacity: 0;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  /* 24px type and a 44px-tall target; was 15px in a 19px-tall button (#245). */
  font-size: 2.4rem;
  min-height: 4.4rem;
  padding: 0.8rem 1.2rem;
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      color: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2rem;
`;

const SocialContainer = styled.div`
  display: flex;
  gap: 2rem;
  animation: ${slideIn} 0.4s ease-in-out forwards;
  animation-delay: 0.5s;
  opacity: 0;
`;

const LanguageSwitcherContainer = styled.div`
  animation: ${slideIn} 0.4s ease-in-out forwards;
  animation-delay: 0.6s;
  opacity: 0;
`;

const SocialLink = styled.a`
  color: ${({ theme }) => theme.colors.white};
  transition: ${({ theme }) => theme.transitions.default};

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      color: ${({ theme }) => theme.colors.accent};
      transform: scale(1.1);
    }
  }
`;

/* Component */
const MobileNav = () => {
  const { social = {}, messages = {} } = useContent();
  const { menuOpen, toggleMenu } = useMenuContext();
  const { handleSectionChange } = useNavigation();
  const socialIconSize = 28;

  // Escape closes the drawer from anywhere (#245).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') toggleMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen, toggleMenu]);

  if (!menuOpen) return null;

  const navItems = [
    { id: 'hero', label: messages?.navigation?.home || 'Home' },
    { id: 'menu', label: messages?.navigation?.menu || 'Menu' },
    { id: 'location', label: messages?.navigation?.location || 'Location' },
    { id: 'about', label: messages?.navigation?.about || 'About' },
  ];

  const handleNavClick = (sectionId) => {
    handleSectionChange(sectionId);
    toggleMenu();
  };

  return (
    <Overlay>
      <CloseButtonContainer>
        <MenuButtonMobile onClick={toggleMenu} isOpen={menuOpen} />
      </CloseButtonContainer>

      <NavContent>
        <NavList>
          {navItems.map((item, index) => (
            <NavItem key={item.id} index={index}>
              <NavButton onClick={() => handleNavClick(item.id)}>
                {item.label}
              </NavButton>
            </NavItem>
          ))}
        </NavList>

        <BottomSection>
          <LanguageSwitcherContainer>
            <LanguageSwitcher />
          </LanguageSwitcherContainer>

          <SocialContainer>
            <SocialLink
              href={social.instagram}
              aria-label="Instagram"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram size={socialIconSize} />
            </SocialLink>
            <SocialLink
              href={social.facebook}
              aria-label="Facebook"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Facebook size={socialIconSize} />
            </SocialLink>
            <SocialLink
              href={social.yelp}
              aria-label="Yelp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Utensils size={socialIconSize} />
            </SocialLink>
          </SocialContainer>
        </BottomSection>
      </NavContent>
    </Overlay>
  );
};

export default MobileNav;
